import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Image, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { usePortalSid } from './PortalLayout';

export function GaleriaPortal() {
  const { user } = useAuth();
  const portalSid = usePortalSid();
  const sid = portalSid ?? user?.solicitanteId ?? '';
  const [searchParams] = useSearchParams();
  const asSuffix = searchParams.get('as') ? `?as=${searchParams.get('as')}` : '';
  const eventosQ = useQuery({ queryKey: ['portal', 'eventos'], queryFn: () => api.eventos.listPortal().then(r => r.data) });
  const detalhesQ = useQuery({ queryKey: ['portal', 'detalhes-list'], queryFn: () => api.detalhesEvento.list().then(r => r.data) });

  const meusRealizados = useMemo(() =>
    (eventosQ.data ?? [])
      .filter(e => (e.solicitantesIds ?? []).includes(sid))
      .filter(e => e.status === 'Realizado'),
    [eventosQ.data, sid]);

  const galeriaPorEvento = useMemo(() => {
    const m = new Map<string, string | undefined>();
    for (const d of detalhesQ.data ?? []) {
      if (d.eventoId && d.solicitanteId === sid && d.urlGaleriaPosEvento) {
        m.set(d.eventoId, d.urlGaleriaPosEvento);
      }
    }
    return m;
  }, [detalhesQ.data, sid]);

  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-2xl font-bold text-text">Galeria</h1>
        <p className="text-sm text-text-2">Fotos dos eventos já realizados</p>
      </header>

      {meusRealizados.length === 0 ? (
        <div className="bg-panel border border-border rounded-xl p-12 text-center text-muted">
          <Image className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Nenhum evento realizado ainda no seu plano.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {meusRealizados.map(e => {
            const url = galeriaPorEvento.get(e.id);
            const Card = (
              <div className="bg-panel border border-border rounded-xl overflow-hidden hover:border-blue/50 transition-colors">
                <div className="aspect-video bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] flex items-center justify-center text-white font-bold">
                  {url ? <Image className="w-10 h-10 opacity-70" /> : <span className="text-xs uppercase tracking-wider opacity-70">Em breve</span>}
                </div>
                <div className="p-3">
                  <div className="text-sm font-bold text-text truncate">{e.nome}</div>
                  <div className="text-[11px] text-text-2 mt-0.5">
                    {e.dataInicio && new Date(e.dataInicio).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </div>
                  {url ? (
                    <span className="text-[10.5px] text-blue-light font-bold mt-2 inline-flex items-center gap-1">
                      Ver galeria <ExternalLink className="w-2.5 h-2.5" />
                    </span>
                  ) : (
                    <span className="text-[10.5px] text-muted italic mt-2 inline-block">Galeria sendo finalizada</span>
                  )}
                </div>
              </div>
            );
            return url
              ? <a key={e.id} href={url} target="_blank" rel="noreferrer">{Card}</a>
              : <Link key={e.id} to={`/portal/eventos/${e.id}${asSuffix}`}>{Card}</Link>;
          })}
        </div>
      )}
    </div>
  );
}
